// Opportunity model
import proxy from "api/proxy";
import { getStorage } from "store/local";
import { layout as layoutLine } from "./ModelOpportunityLine";
import { layout as layoutActivity } from "./layoutActivityOpportunityLine";
import { layout as layoutProspect } from "./layoutProspect";
import { layoutParty } from "../Partyv2/Party";
import CardItemOpportunity from "./CardItemOpportunity";
import StateFilter from "./components/StateFilter";
import dates from "tools/dates";
import {isValidPhone, isValidName, isValidEmail} from "tools/helpers";

const stateColors = {
  lead: {
    header: "bg-neutral-800",
    card: "bg-neutral-50",
    textHeader: "!text-neutral-100",
  },
  opportunity: {
    header: "bg-sky-300",
    card: "bg-sky-50",
    textHeader: "!text-sky-800",
  },
  converted: {
    header: "bg-amber-300",
    card: "bg-amber-50",
    textHeader: "!text-amber-900",
  },
  won: {
    header: "bg-lime-300",
    card: "bg-lime-50",
    textHeader: "!text-lime-900",
  },
  lost: {
    header: "bg-rose-300",
    card: "bg-rose-50",
    textHeader: "!text-rose-900",
  },
  cancelled: {
    header: "bg-indigo-300",
    card: "bg-indigo-50",
    textHeader: "!text-indigo-900",
  },
};

const stateTags = {
  lead: "slate",
  opportunity: "sky",
  converted: "amber",
  won: "lime",
  lost: "rose",
  cancelled: "indigo",
};

const TYPE_OPP = [
  { id: "sale", name: "Venta" },
  { id: "contract", name: "Contrato" },
];

function onSendMail(rec) {
  const email = rec.party?.email | "gerente@presik.com";
  return email;
}

function invisibleButton(btn, record) {
  if (!record?.state) return true;
  const state = record.state;
  if (btn === "opportunity")
    return !["lead", "converted", "cancelled"].includes(state);
  if (btn === "converted")
    return !["opportunity", "won", "lost", "cancelled"].includes(state);
  if (btn === "won" || btn === "lost") return state !== "converted";
  if (btn === "cancelled") return !["opportunity", "converted"].includes(state);
  return false;
}

const getAgent = async () => {
  const session = getStorage("ctxSession");
  const dom = [["user", "=", session.user]];
  const fields = ["id", "rec_name"];
  const { data } = await proxy.search("commission.agent", dom, fields, 1);
  return data?.[0];
};

const getDataProspect = async (_, record) => {
  const prospect = record.store?.prospect || record.active?.prospect;
  if (!prospect) return;
  record.store.contact_email = prospect.email;
  record.active.contact_email = prospect.email;
  record.store.contact_phone = prospect.phone;
  record.active.contact_phone = prospect.phone;
  record.store.contact_company = prospect.company;
  record.active.contact_company = prospect.company;
  record.store.lead_origin = prospect["lead_origin."];
  record.active.lead_origin = prospect["lead_origin."];
  record.store.party_category = prospect["party_category."];
  record.active.party_category = prospect["party_category."];
};

const getOpportunityCities = async (q) => {
  const { data } = await proxy.search("crm.opportunity", [], ["city"]);
  const ids = [...new Set(data?.map((o) => o.city).filter(Boolean))];

  const domain = [["id", ids.length ? "in" : "=", ids.length ? ids : 0]];
  if (q) domain.push(["rec_name", "ilike", `%${q}%`]);

  return domain;
};

function layout(config = {}) {
  const ctxView = {
    model: "crm.opportunity",
    activeSearch: true,
    domain: [],
    orderBy: [
      ["start_date", "DESC"],
    ],
    availableViews: ["list", "column"],
    display: "modal",
    states: stateColors,
    kanbanStatusField: "state",
    kanbanDateField: "start_date",
    kanbanTitle: "crm_opportunities_title",
    kanbanHideTimes: true,
    kanbanHideSearch: true,
    kanbanShowToolbar: false,
    kanbanShowOptions: false,
    kanbanUseModal: false,
    kanbanHorizontal: true,
    kanbanUseHeaderColumn: false,
    kanbanCardComponent: CardItemOpportunity,
    multi_filter: true,
    tableStateComponent: StateFilter,
    filtersConfig: {
      city: getOpportunityCities,
    },
    title: { field: "number", model: true },
    form_action: ["add", "edit"],
    table_action: ["open", "add"],
    limit: 50,
    reports: [
      {
        name: "crm.opportunity",
        label: "crm.opportunity.report",
        send_print: true,
        send_email: onSendMail,
      },
    ],
    transitions: [
      ["lead", "opportunity"],
      ["opportunity", "converted"],
      ["opportunity", "cancelled"],
      ["cancelled", "opportunity"],
      ["cancelled", "converted"],
      ["converted", "cancelled"],
      ["converted", "opportunity"],
      ["converted", "won"],
      ["converted", "lost"],
      ["won", "converted"],
      ["lost", "converted"],
    ],
    tags: config.tags ?? {
      state: stateTags,
    },
    fields: {
      number: {
        type: "char",
        readOnly: true,
      },
      reference: { type: "char" },
      party_contact: {
        type: "char",
        searchable: true,
        validate: ({ value }) => {
          if (!value) return true;
          return isValidName(value)
            ? true
            : "party.party.validation.invalid_name";
        },
      },
      contact_phone: {
        type: "char",
        required: true,
        validate: ({ value }) => {
          if (!value) return true;
          return isValidPhone(value)
            ? true
            : "party.party.validation.invalid_phone";
        },
      },
      contact_email: {
        type: "char",
        validate: ({ value }) => {
        if (!value) return true;
        return isValidEmail(value)
          ? true
          : "party.party.validation.invalid_email";
        }
      },
      party_category: {
        type: "many2one",
        model: "party.category",
        filter: true,
      },
      contact_company: {
        type: "char",
        searchable: true,
        uppercase: true,
      },
      prospect: {
        type: "many2one",
        model: "crm.prospect",
        layout: layoutProspect(),
        withChange: getDataProspect,
        searchable: true,
        attrs: ["email", "phone", "agent", "lead_origin", "party_category", "company"
        ],
      },
      start_date: {
        type: "date_2",
        required: true,
        default: dates.dateToday(),
        filter: "range",
        readOnly: (rec) => rec?.id > 0,
      },
      end_date: { type: "date_2", filter: true },
      create_uid: {
        type: "many2one",
        model: "res.user",
        readOnly: true,
      },
      lead_origin: {
        type: "many2one",
        model: "crm.lead_origin",
        filter: true,
        required: true,
      },
      agent: {
        type: "many2one",
        model: "commission.agent",
        default: getAgent,
        filter: true,
      },
      payment_term: {
        type: "many2one",
        model: "account.invoice.payment_term",
      },
      conversion_probability: {
        type: "numeric",
        default: 1,
        required: true,
        symbol: "%",
      },
      state: {
        type: "char",
        readOnly: true,
        translate: true,
        default: "lead",
        tags: stateTags,
      },
      party: {
        type: "many2one",
        model: "party.party",
        searchable: true,
        attrs: ["account_receivable"],
        layout: layoutParty(),
      },
      city: {
        type: "many2one",
        model: "country.city",
        filter: true,
        searchable: true,
      },
      contact: {
        type: "many2one",
        model: "party.contact_mechanism",
      },
      address: {
        type: "many2one",
        model: "party.address",
      },
      type: {
        type: "selection",
        options: TYPE_OPP,
        required: true,
        default: { id: "sale", name: "sale" },
      },
      amount: {
        type: "numeric",
        decimalPlaces: 2,
      },
      description: { type: "char", required: true },

      lines: {
        type: "one2many",
        model: "crm.opportunity.line",
        layout: layoutLine(),
      },
      activities: {
        type: "one2many",
        model: "crm.activity",
        layout: layoutActivity({
          filters: [],
          tags: {},
          showActions: false,
        }),
      },
      comment: { type: "text" },
      opportunity: {
        type: "button",
        button_method: "opportunity",
        invisible: invisibleButton,
        color: "sky",
      },
      converted: {
        type: "button",
        button_method: "converted",
        invisible: invisibleButton,
        color: "amber",
      },
      won: {
        type: "button",
        button_method: "won",
        invisible: invisibleButton,
        color: "green",
      },
      lost: {
        type: "button",
        button_method: "lost",
        invisible: invisibleButton,
        color: "rose",
      },
      cancelled: {
        type: "button",
        button_method: "cancelled",
        invisible: invisibleButton,
        color: "indigo",
      },
    },
    list: [
      { name: "number", width: "10%" },
      { name: "party", width: "15%" },
      { name: "prospect", width: "15%" },
      { name: "contact_company", width: "15%" },
      { name: "party_category", width: "15%" },
      { name: "city", width: "15%" },
      { name: "lead_origin", width: "15%" },
      { name: "start_date", width: "10%" },
      { name: "conversion_probability", width: "10%" },
      { name: "state", width: "5%", widget: "circle" },
    ],
    form: [
      {
        name: "block0",
        type: "grid",
        col: "grid-cols-2 ",
        colSpan: "col-span-2 md:col-span-1",
        elements: [
          { name: "prospect", widget: "search" },
          { name: "party", widget: "search" },
        ],
      },
      {
        name: "block1",
        type: "grid",
        col: "grid-cols-2",
        colSpan: "col-span-2 md:col-span-1",
        elements: [{ name: "number" }, { name: "state" }],
      },
      {
        name: "block2",
        type: "grid",
        col: "grid-cols-2",
        colSpan: "col-span-2 md:col-span-1",
        elements: [{ name: "city" }, { name: "contact_phone" }],
      },
      {
        name: "block21",
        type: "grid",
        col: "grid-cols-2",
        colSpan: "col-span-2 md:col-span-1",
        elements: [{ name: "start_date" }, { name: "end_date" }],
      },
      {
        name: "block3",
        type: "grid",
        col: "grid-cols-3",
        colSpan: "col-span-2 md:col-span-1",
        elements: [{ name: "party_contact" }, { name: "contact_email" }, { name: "contact_company" }],
      },
      {
        name: "block4",
        type: "grid",
        col: "grid-cols-2",
        colSpan: "col-span-2 md:col-span-1",
        elements: [{ name: "party_category" }, { name: "payment_term" }],
      },
      {
        name: "block5",
        type: "grid",
        col: "grid-cols-2",
        colSpan: "col-span-2 md:col-span-1",
        elements: [{ name: "agent" }, { name: "lead_origin" }],
      },
      {
        name: "block6",
        type: "grid",
        colSpan: "col-span-2 md:col-span-1",
        elements: [{ name: "description" }, { name: "conversion_probability" }],
      },
      {
        name: "block7",
        type: "grid",
        colSpan: "col-span-2",
        elements: [{ name: "lines" }, { name: "activities" }],
      },
      {
        name: "block8",
        type: "grid",
        colSpan: "col-span-2",
        elements: [{ name: "comment" }],
      },
      {
        name: "actions",
        type: "grid",
        cols: "grid-cols-5",
        colSpan: "col-span-2",
        elements: [
          { name: "cancelled" },
          { name: "opportunity" },
          { name: "converted" },
          { name: "lost" },
          { name: "won" },
        ],
      },
    ],
  };

  return ctxView;
}

export { layout };
